import { useEffect, useState } from "react"
import {
  buildVitalViews,
  nextChangeMs,
  serverNowMs,
  vitalViewsEqual
} from "@/helpers/vitals"
import { useHasSession } from "@/hooks/useHasSession"
import { useAppSelector } from "@/store"
import { useGetHeroMeQuery } from "@/store/apis/heroAPI"
import { PlayerSelectors } from "@/store/slices/player"
import { IVitalView } from "@/types/vitals.types"

const EMPTY_VIEWS: IVitalView[] = []

export const useHeroVitals = (): IVitalView[] => {
  const hasSession = useHasSession()
  const isPlayerLoaded = useAppSelector(PlayerSelectors.selectIsPlayerLoaded())

  const { vitals, serverTime, receivedAtMs } = useGetHeroMeQuery(undefined, {
    skip: !hasSession || !isPlayerLoaded,
    selectFromResult: ({ data, fulfilledTimeStamp }) => ({
      vitals: data?.vitals,
      serverTime: data?.serverTime,
      receivedAtMs: fulfilledTimeStamp
    })
  })

  const [views, setViews] = useState<IVitalView[]>(EMPTY_VIEWS)

  useEffect(() => {
    if (!vitals || !serverTime || receivedAtMs === undefined) {
      setViews(EMPTY_VIEWS)
      return
    }

    let timerId: number | undefined

    const project = () => {
      const nowMs = serverNowMs(serverTime, receivedAtMs, Date.now())
      const next = buildVitalViews(vitals, nowMs)
      setViews((prev) => (vitalViewsEqual(prev, next) ? prev : next))
      const changeAt = nextChangeMs(vitals, nowMs)
      if (changeAt !== null) {
        timerId = window.setTimeout(project, Math.max(1, changeAt - nowMs))
      }
    }

    project()

    return () => window.clearTimeout(timerId)
  }, [vitals, serverTime, receivedAtMs])

  return views
}
